<template>
  <div v-if="flows.length > 0">
    <loading-view :loading="initialLoading">
      <form @submit.prevent @keydown.enter="$emit('submit')">
        <!-- Flow Settings-->
        <div class="grid grid-2">
          <div>
            <div v-for="flow in flows" :key="`flow-${flow.id}`" class="mb-3">
              <form-input-header
                class="p-2 bg-theme-primary"
                :header="flow.first_flow.group.name"
                header-class="mb-0"
              />
              <div class="px-2">
                <div v-if="getError(flow.id).length > 0" class="mt-2">
                  <validation-errors :errors="getError(flow.id)" />
                </div>
                <div v-for="category in categories[flow.id]" :key="category.name">
                  <div
                    v-for="(settingsFromGetSettings, settingGroup) in getSettingsData(flow.id, category.name)"
                    :key="settingGroup"
                    class="w-full"
                  >
                    <form-input
                      v-for="setting in settingsFromGetSettings"
                      :key="`${flow.id}-${setting.key}`"
                      v-model="formData[flow.id][setting.id].value"
                      :dependencies="setting.dependency_settings"
                      :help-text="setting.description"
                      :label="setting.name"
                      :multiple="setting.is_multiple"
                      :options="setting.application_setting_options"
                      :required="setting.is_required"
                      :setting="setting"
                      :type="setting.field_type_name"
                      :integrationFlowId="flow.id"
                      :application1="applicationInfo1"
                      :application2="applicationInfo2"
                      :dependency-values="formData[flow.id]"
                      @input-dependency="handleInputDependency(flow.id, $event)"
                      :unique-suffix="`dependency-${flow.id}`"
                    >
                    </form-input>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </loading-view>
  </div>
</template>

<script>
import HandleSettings from '@/components/mixins/HandleSettings';
import FormInput from '@/components/Dashboard/FormInput';
import FormInputHeader from '@/components/Dashboard/FormInputHeader';
import MappingTable from '@/components/Dashboard/MappingTable';
import ValidationErrors from '@/components/Shared/ValidationErrors';

export default {
  name: 'WizardSettings',

  mixins: [HandleSettings],

  components: {
    ValidationErrors,
    FormInput,
    FormInputHeader,
    MappingTable,
  },

  props: {
    user: Object,
    applicationInfo1: Object,
    applicationInfo2: Object,
    integration: Object,
    flows: Array,
  },

  data() {
    return {
      formData: {},
      settings: {},
      categories: {},
      savedSettings: {},
      processingSave: false,
      initialLoading: true,
      allSettings: {},
      allDependencySettings: {},
      errors: [],
    };
  },

  watch: {
    async flows() {
      if (!this.initialLoading) {
        await this.prepareData();
      }
    },
  },

  methods: {
    handleError(error) {
      this.errors = error.errors;
    },

    getSettingsData(integrationFlowId, categoryName) {
      return this.settings[integrationFlowId][categoryName];
    },

    getSavedSettingsValue(flowsKey, settingId, isDependency = false) {
      const setting = _.find(_.toArray(this.savedSettings[flowsKey]), { setting_id: settingId });

      let settingData = null;
      if (isDependency) {
        settingData = _.find(_.toArray(this.allDependencySettings[flowsKey]), { id: settingId });
      } else {
        settingData = _.find(_.toArray(this.allSettings[flowsKey]), { id: settingId });
      }

      if (setting && setting.value == null) {
        return settingData.default_value;
      }

      return setting ? setting.value : settingData.default_value;
    },

    async prepareData() {
      this.initialLoading = true;
      await axios
        .get(`/user/${this.user.id}/user-integration/${this.integration.id}/flows/settings`)
        .then(({ data }) => {
          const flows = data.data.flows;

          for (const flowsKey in flows) {
            if (flows.hasOwnProperty(flowsKey)) {
              const flow = flows[flowsKey];
              const userIntegrationFlowId = flow.userIntegrationFlowId;

              this.settings[userIntegrationFlowId] = flow.settings;
              this.categories[userIntegrationFlowId] = flow.categories;
              this.savedSettings[userIntegrationFlowId] = flow.savedSettings;
              this.allSettings[userIntegrationFlowId] = [];
              this.formData[userIntegrationFlowId] = {};
              this.allDependencySettings[userIntegrationFlowId] = [];

              Object.keys(this.settings[userIntegrationFlowId]).forEach(categorySetting => {
                Object.keys(this.settings[userIntegrationFlowId][categorySetting]).forEach(groupSetting => {
                  this.settings[userIntegrationFlowId][categorySetting][groupSetting].forEach(setting => {
                    this.allSettings[userIntegrationFlowId].push(setting);

                    this.formData[userIntegrationFlowId][setting.id] = {};
                    this.formData[userIntegrationFlowId][setting.id].setting_id = setting.id;
                    this.formData[userIntegrationFlowId][setting.id].format_date = setting.format_date;
                    this.formData[userIntegrationFlowId][setting.id].value = this.getSavedSettingsValue(
                      userIntegrationFlowId,
                      setting.id,
                    );

                    if (Array.isArray(setting.dependency_settings) && setting.dependency_settings.length > 0) {
                      const dependencies = setting.dependency_settings;

                      for (const dependenciesKey in dependencies) {
                        const dependency = dependencies[dependenciesKey];

                        this.allDependencySettings[userIntegrationFlowId].push(dependency);
                        this.formData[userIntegrationFlowId][dependency.id] = {};
                        this.formData[userIntegrationFlowId][dependency.id].setting_id = dependency.id;
                        this.formData[userIntegrationFlowId][dependency.id].format_date = dependency.format_date;
                        this.formData[userIntegrationFlowId][dependency.id].value = this.getSavedSettingsValue(
                          userIntegrationFlowId,
                          dependency.id,
                          true,
                        );
                      }
                    }
                  });
                });
              });
            }
          }
        });

      const ids = _.map(this.flows, 'id');

      this.initialLoading = false;
    },

    async saveSettings() {
      await axios
        .patch(`/user-integration-flow-settings/`, {
          settings: this.formData,
        })
        .then(() => {
          this.errors = [];
        })
        .catch(({ response }) => this.handleError(response.data));

      return this.errors.length < 1;
    },

    handleInputDependency(flowId, event) {
      this.formData[flowId][event.dependencyId].value = event.value;
    },

    getError(flowId) {
      return _.uniq(_.flatten(this.errors[flowId] || []));
    },
  },
};
</script>
